import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  ID_VERIFICATION_STATUS_SUCCESS,
  ID_VERIFICATION_STATUS_FAILED,
  ID_VERIFICATION_FLOW_RENTAL_APPLICATION,
} from 'common/idVerificationConstants';

import {
  SR_API_ACTION_ID_VERIFICATION_GET_BY_USER_SESSION,
  SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
  SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_STEP,
} from 'api/actionTypes';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';
import IdVerificationsActions from 'client/actions/IdVerifications';
import SrIdVerificationModal from 'client/components/SrIdVerificationModal';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

const debug = log.debug('sunroom:components:SrSelfShowingVerificationModal');

@withApiState(({ userId }) => ({
  getIdVerificationByUserSession: {
    action: SR_API_ACTION_ID_VERIFICATION_GET_BY_USER_SESSION,
    queryId: String(userId),
  },
}))
@branch(({ userId }) => ({
  userIdVerification: ['idVerifications', 'byUserId', userId],
}))
class SrSelfShowingVerificationModal extends Component {
  static propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    listingId: PropTypes.number.isRequired,
    getIdVerificationByUserSession: PropTypes.object.isRequired,
    userIdVerification: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    idVerificationFlow: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this._vouchedToken = null;
  }

  componentDidMount() {
    const { dispatch, userIdVerification, idVerificationFlow } = this.props;

    if (idVerificationFlow === ID_VERIFICATION_FLOW_RENTAL_APPLICATION) {
      return;
    }

    if (userIdVerification) {
      this._onUserIdVerificationLoaded(userIdVerification);
    } else {
      dispatch(IdVerificationsActions.getByUserSession);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { userIdVerification, idVerificationFlow } = this.props;
    if (idVerificationFlow === ID_VERIFICATION_FLOW_RENTAL_APPLICATION) {
      return;
    }

    if (!userIdVerification && nextProps.userIdVerification) {
      this._onUserIdVerificationLoaded(nextProps.userIdVerification);
    }
  }

  _onUserIdVerificationLoaded = (userIdVerification) => {
    const { onSuccess } = this.props;
    if (userIdVerification.status === ID_VERIFICATION_STATUS_SUCCESS) {
      onSuccess(userIdVerification);
    }
  };

  _onVouchedInit = ({ token }) => {
    const { listingId } = this.props;
    debug('init', { token, thisVouchedToken: this._vouchedToken });
    if (this._vouchedToken === token) {
      return;
    }
    this._vouchedToken = token;
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
      payload: {
        vouchedToken: token,
        listingId: listingId,
      },
    });
  };

  _onVouchedCamera = ({ hasCamera, hasPermission }) => {
    const { dispatch } = this.props;
    debug('onCamera', { hasCamera, hasPermission });
    dispatch(postWithState, {
      action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_STEP,
      payload: {
        vouchedToken: this._vouchedToken,
        stage: 'camera',
        attempts: 1,
        vouchedPayload: {
          hasCamera,
          hasPermission,
          result: {
            success: hasCamera && hasPermission,
          },
        },
      },
    });
  };

  _onVouchedSubmit = ({ stage, attempts, job }) => {
    const { dispatch } = this.props;
    const payload = {
      vouchedToken: this._vouchedToken,
      stage: stage,
      attempts: attempts,
      vouchedPayload: job,
    };
    debug('submit', payload);
    dispatch(postWithState, {
      action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_STEP,
      payload: payload,
    });
  };

  _onVouchedDone = (job) => {
    const { idVerificationFlow, onSuccess, onFailure, dispatch } = this.props;
    debug('Scanning complete', { token: job.token });
    dispatch(IdVerificationsActions.submit, {
      vouchedToken: job.token,
      idVerificationFlow,
    }).then((idVerification) => {
      debug('_onVouchedDone', idVerification);
      switch (idVerification.status) {
        case ID_VERIFICATION_STATUS_SUCCESS: {
          onSuccess(idVerification);
          break;
        }
        case ID_VERIFICATION_STATUS_FAILED: {
          onFailure(idVerification);
          break;
        }
        default: {
          const error = new Error(`ID verification status not supported: ${idVerification.status}`);
          error.name = 'SrIdVerificationStatusNotSupportedError';
          error.details = {
            idVerification,
          };
          throw error;
        }
      }
    });
  };

  render() {
    const { getIdVerificationByUserSession, onClose, crossCheckVerification } = this.props;

    if (getIdVerificationByUserSession.inProgress) {
      return null;
    }

    return (
      <SrDarkModal onClose={onClose}>
        <SrModalContent onCloseIconClick={onClose}>
          <SrIdVerificationModal
            crossCheckVerification={crossCheckVerification}
            onInit={this._onVouchedInit}
            onCamera={this._onVouchedCamera}
            onSubmit={this._onVouchedSubmit}
            onDone={this._onVouchedDone}
          />
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrSelfShowingVerificationModal.displayName = 'SrSelfShowingVerificationModal';

export default SrSelfShowingVerificationModal;
